import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, Calendar, Video, X, Check, Users, AlertCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map notification types to icons and colors
const NOTIFICATION_STYLES = {
  meeting: { icon: Video, bg: "bg-blue-100", text: "text-blue-600" },
  event_invite: { icon: Calendar, bg: "bg-violet-100", text: "text-violet-600" },
  event_update: { icon: Calendar, bg: "bg-orange-100", text: "text-orange-600" },
  event_cancelled: { icon: AlertCircle, bg: "bg-red-100", text: "text-red-600" },
  facilitator_added: { icon: Users, bg: "bg-green-100", text: "text-green-600" },
  default: { icon: Bell, bg: "bg-gray-100", text: "text-gray-600" }
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      
      const response = await fetch(`${API_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_URL}/api/notifications/${notificationId}/read`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_URL}/api/notifications/read-all`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diff = now - date;
      
      if (diff < 60000) return "Just now";
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
      if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
      
      return date.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    } catch {
      return "";
    }
  };

  const formatMeetingDate = (dateStr, timeStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const formatted = date.toLocaleDateString("en-IN", { 
        weekday: "short", day: "numeric", month: "short" 
      });
      // If no explicit timeStr, try to extract from ISO date
      if (!timeStr && dateStr.includes("T")) {
        const time = date.toLocaleTimeString("en-IN", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true
        });
        return `${formatted} at ${time}`;
      }
      return timeStr ? `${formatted} at ${timeStr}` : formatted;
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="sm"
        className="relative"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border z-50 max-h-[70vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={markAllAsRead}
                disabled={loading}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                <Check className="w-3 h-3 mr-1" />
                Mark all read
              </Button>
            )}
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y">
                {notifications.map((notification) => {
                  const style = NOTIFICATION_STYLES[notification.notification_type] || NOTIFICATION_STYLES.default;
                  const IconComponent = style.icon;
                  const hasEventLink = notification.metadata?.event_id;
                  
                  const handleNotificationClick = () => {
                    if (!notification.is_read) {
                      markAsRead(notification.id);
                    }
                    // Navigate to event if it's an event notification
                    if (hasEventLink) {
                      setIsOpen(false);
                      navigate(`/events/public/${notification.metadata.event_id}`);
                    }
                  };
                  
                  return (
                    <div
                      key={notification.id}
                      className={`p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                        !notification.is_read ? "bg-blue-50" : ""
                      }`}
                      onClick={handleNotificationClick}
                      data-testid={`notification-item-${notification.id}`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Icon */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${style.bg} ${style.text}`}>
                          <IconComponent className="w-5 h-5" />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm ${!notification.is_read ? "font-medium" : ""}`}>
                              {notification.title}
                            </p>
                            <span className="text-xs text-gray-400 whitespace-nowrap">
                              {formatDate(notification.created_at)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-0.5">
                            {notification.message}
                          </p>
                          
                          {/* Event Details */}
                          {notification.metadata?.start_at && (
                            <div className="mt-2 p-2 bg-gray-50 rounded-lg text-xs">
                              <div className="flex items-center gap-1 text-gray-700">
                                <Calendar className="w-3 h-3" />
                                {formatMeetingDate(notification.metadata.start_at)}
                              </div>
                              {notification.metadata.event_type && (
                                <span className="text-gray-500 capitalize">
                                  {notification.metadata.event_type.replace("_", " ")}
                                </span>
                              )}
                            </div>
                          )}
                          
                          {/* Legacy Meeting Details */}
                          {notification.metadata?.meeting_date && !notification.metadata?.start_at && (
                            <div className="mt-2 p-2 bg-gray-50 rounded-lg text-xs">
                              <div className="flex items-center gap-1 text-gray-700">
                                <Calendar className="w-3 h-3" />
                                {formatMeetingDate(notification.metadata.meeting_date, notification.metadata.meeting_time)}
                              </div>
                              {notification.metadata.meeting_link && (
                                <a 
                                  href={notification.metadata.meeting_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline mt-1 block truncate"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {notification.metadata.meeting_link}
                                </a>
                              )}
                            </div>
                          )}
                          
                          {/* Action hint for event notifications */}
                          {hasEventLink && (
                            <p className="mt-1 text-xs text-violet-600">Click to view event</p>
                          )}
                          
                          {/* Unread indicator */}
                          {!notification.is_read && (
                            <Badge className="mt-2 bg-blue-100 text-blue-700 text-xs">New</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
